import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Plus, X, Loader2, Save, Edit2, Trash2, CheckCircle2 } from 'lucide-react';
import api from '../services/api';
import { uyar, hata, onayla } from '../components/GlobalModal';

interface Catering {
  cateringID: number;
  code: string;
  name: string;
  contactPerson?: string;
  phone?: string;
  email?: string;
  address?: string;
  notes?: string;
  isActive: boolean;
}

const MealCatering: React.FC = () => {
  const [rows, setRows] = useState<Catering[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Catering | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    code: '',
    name: '',
    contactPerson: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
    isActive: true,
  });

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r: any = await api.get('/meals/catering');
      const raw = r?.data ?? r;
      const arr = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
      setRows(arr);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Firmalar yüklenemedi');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ code: '', name: '', contactPerson: '', phone: '', email: '', address: '', notes: '', isActive: true });
    setShowModal(true);
  };
  const openEdit = (c: Catering) => {
    setEditing(c);
    setForm({
      code: c.code ?? '',
      name: c.name ?? '',
      contactPerson: c.contactPerson ?? '',
      phone: c.phone ?? '',
      email: c.email ?? '',
      address: c.address ?? '',
      notes: c.notes ?? '',
      isActive: c.isActive !== false,
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name.trim()) { uyar('Firma adı zorunlu'); return; }
    setSaving(true); setError(null);
    try {
      const body: any = {
        code: form.code || form.name.substring(0, 3).toUpperCase(),
        name: form.name,
        contactPerson: form.contactPerson || null,
        phone: form.phone || null,
        email: form.email || null,
        address: form.address || null,
        notes: form.notes || null,
        isActive: form.isActive,
      };
      if (editing) {
        await api.put(`/meals/catering/${editing.cateringID}`, body);
      } else {
        await api.post('/meals/catering', body);
      }
      setShowModal(false);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Kaydedilemedi');
    } finally { setSaving(false); }
  };

  const remove = async (c: Catering) => {
    if (!(await onayla(`"${c.name}" silinsin mi?`))) return;
    try {
      await api.delete(`/meals/catering/${c.cateringID}`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Silinemedi');
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-800 flex items-center gap-2">
            <Building2 size={26} className="text-blue-600" /> Yemek Firmaları
          </h1>
          <p className="text-sm text-slate-500 mt-1">Yemek hizmeti veren catering firmaları (DOĞAN, BEREKET vb.)</p>
        </div>
        <button onClick={openNew}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700">
          <Plus size={16} /> Yeni Firma
        </button>
      </div>

      {error && <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>}

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] font-black text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-3">Kod</th>
                <th className="px-4 py-3">Firma Adı</th>
                <th className="px-4 py-3">İlgili Kişi</th>
                <th className="px-4 py-3">Telefon</th>
                <th className="px-4 py-3">E-posta</th>
                <th className="px-4 py-3">Durum</th>
                <th className="px-4 py-3 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-16 text-center text-slate-400"><Loader2 size={20} className="animate-spin mx-auto" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-16 text-center text-slate-400">
                  <Building2 size={32} className="mx-auto opacity-30 mb-2" /> Tanımlı firma yok
                </td></tr>
              ) : rows.map(c => (
                <tr key={c.cateringID} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-slate-600">{c.code}</td>
                  <td className="px-4 py-3 font-bold text-slate-800">{c.name}</td>
                  <td className="px-4 py-3 text-slate-600">{c.contactPerson ?? '—'}</td>
                  <td className="px-4 py-3 font-mono text-slate-600">{c.phone ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{c.email ?? '—'}</td>
                  <td className="px-4 py-3">
                    {c.isActive ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">
                        <CheckCircle2 size={10} /> Aktif
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold">Pasif</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openEdit(c)} className="mr-2 text-blue-600 hover:text-blue-800"><Edit2 size={14} /></button>
                    <button onClick={() => remove(c)} className="text-red-600 hover:text-red-800"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between bg-orange-600 text-white px-6 py-4 rounded-t-2xl">
              <h3 className="font-black text-lg">{editing ? 'Firma Düzenle' : 'Yeni Yemek Firması'}</h3>
              <button onClick={() => setShowModal(false)} className="hover:bg-white/20 rounded p-1"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-3 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Kod</label>
                  <input type="text" value={form.code} onChange={e => setForm({ ...form, code: e.target.value })}
                    placeholder="Boş = otomatik" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Firma Adı *</label>
                  <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">İlgili Kişi</label>
                <input type="text" value={form.contactPerson} onChange={e => setForm({ ...form, contactPerson: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Telefon</label>
                  <input type="text" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">E-posta</label>
                  <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Adres</label>
                <textarea value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                  rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Notlar</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                  rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} className="w-4 h-4" />
                Aktif
              </label>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-slate-100 p-4">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-bold text-slate-700 hover:bg-slate-50">İptal</button>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-bold hover:bg-orange-700 disabled:opacity-60">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MealCatering;
